
import { useSettingsStore } from '@/state/useSettingsStore'
import { nowInPrague } from './clock'

type Note = { freq: number; durationSec: number; vol?: number; type?: OscillatorType }

const NOTE_FREQ: Record<string, number> = {
  C4: 261.63,
  D4: 293.66,
  E4: 329.63,
  F4: 349.23,
  G4: 392.0,
  A4: 440.0,
  B4: 493.88,
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
  F5: 698.46,
  G5: 783.99,
  A5: 880.0,
  B5: 987.77,
  C6: 1046.5,
  E6: 1318.51,
}

function seq(
  noteNames: Array<keyof typeof NOTE_FREQ | string>,
  durationSec: number,
  type: OscillatorType = 'sine',
): Note[] {
  return noteNames.map((n) => ({
    freq: NOTE_FREQ[n as keyof typeof NOTE_FREQ] ?? 440,
    durationSec,
    type,
  }))
}

const MOTIFS = {
  'new-client': seq(['C5', 'E5', 'G5'], 0.12, 'triangle'),
  'incident-closed': seq(['G4', 'C5'], 0.1, 'sine'),
  'tier-1': seq(['G4', 'C5', 'E5'], 0.15, 'triangle'),
  'tier-2': seq(['C5', 'E5', 'G5', 'C6'], 0.16, 'triangle'),
  'tier-3': seq(['C5', 'E5', 'G5', 'C6', 'E6'], 0.18, 'triangle'),
  'tier-4': seq(['G4', 'C5', 'E5', 'G5', 'C6', 'E6'], 0.2, 'sawtooth'),
  'tier-5': seq(['C5', 'G5', 'C6', 'E6'], 0.28, 'sawtooth'),
  'weekly-intro': seq(['E5', 'G5', 'C6'], 0.18, 'triangle'),
  'monthly-intro': seq(['C5', 'E5', 'G5', 'C6'], 0.22, 'triangle'),
} as const

export type MotifKey = keyof typeof MOTIFS

let ctx: AudioContext | null = null
let unlocked = false

function ensureCtx(): AudioContext {
  if (!ctx) {
    type WindowWithWebkit = Window & {
      webkitAudioContext?: typeof AudioContext
    }
    const win = window as WindowWithWebkit
    const AudioCtor = window.AudioContext || win.webkitAudioContext
    if (!AudioCtor) throw new Error('Web Audio API not available')
    ctx = new AudioCtor()
  }
  return ctx
}

export function isAudioUnlocked(): boolean {
  return unlocked && ctx?.state === 'running'
}

export function unlockAudio(): Promise<void> {
  return new Promise<void>((resolve) => {
    try {
      const c = ensureCtx()
      unlocked = true
      const osc = c.createOscillator()
      const g = c.createGain()
      g.gain.value = 0
      osc.connect(g).connect(c.destination)
      osc.start()
      osc.stop(c.currentTime + 0.001)
      if (c.state === 'suspended') {
        c.resume()
          .catch(() => undefined)
          .finally(() => resolve())
      } else {
        resolve()
      }
    } catch (err) {
      console.warn('audio unlock failed', err)
      resolve()
    }
  })
}

let autoUnlockInstalled = false
export function installAudioAutoUnlock(): void {
  if (autoUnlockInstalled) return
  autoUnlockInstalled = true
  if (typeof document === 'undefined') return
  const handler = () => {
    void unlockAudio()
    if (isAudioUnlocked()) {
      document.removeEventListener('click', handler)
      document.removeEventListener('keydown', handler)
      document.removeEventListener('touchstart', handler)
    }
  }
  document.addEventListener('click', handler, { passive: true })
  document.addEventListener('keydown', handler, { passive: true })
  document.addEventListener('touchstart', handler, { passive: true })
}

function isQuietHour(): boolean {
  const s = useSettingsStore.getState()
  if (!s.soundEnabled) return true
  const h = nowInPrague().getHours()
  const from = s.quietHoursFrom
  const to = s.quietHoursTo
  if (from === to) return false
  if (from < to) return h >= from && h < to
  return h >= from || h < to
}

function effectiveVolume(): number {
  const s = useSettingsStore.getState()
  return isQuietHour() ? Math.min(s.soundVolume, 0.25) : s.soundVolume
}

let lastPlayAt = 0
const MIN_GAP_MS = 100

export function playMotif(key: MotifKey): void {
  if (typeof window === 'undefined') return
  if (!unlocked) return
  if (Date.now() - lastPlayAt < MIN_GAP_MS) return
  lastPlayAt = Date.now()
  const c = ensureCtx()
  if (c.state === 'suspended') {
    c.resume().catch(() => undefined)
  }
  const vol = effectiveVolume()
  if (vol <= 0) return
  const motif = MOTIFS[key]
  let t = c.currentTime + 0.02
  for (const note of motif) {
    const osc = c.createOscillator()
    const gain = c.createGain()
    osc.type = note.type ?? 'sine'
    osc.frequency.setValueAtTime(note.freq, t)
    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(vol * (note.vol ?? 0.4), t + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, t + note.durationSec)
    osc.connect(gain).connect(c.destination)
    osc.start(t)
    osc.stop(t + note.durationSec + 0.05)
    t += note.durationSec * 0.9
  }
}

export function motifForTier(tier: number): MotifKey {
  if (tier >= 5) return 'tier-5'
  if (tier >= 4) return 'tier-4'
  if (tier >= 3) return 'tier-3'
  if (tier >= 2) return 'tier-2'
  return 'tier-1'
}
